import type {
  AllyDef,
  BeatChainDef,
  NpcDef,
  PanelRulesDef,
  PanelSpec,
  PronounSetDef,
  StoryRulesDef,
} from './story/types';

/** shared-cs `StoryRulesDef` defaults (engine.html §3 starting numbers): what the server uses for a bundle without `story`. */
export const storyRulesDefaults = (): Required<StoryRulesDef> => ({
  maxDepth: 4,
  defaultTimerS: 8,
  relaxedTimerMult: 2,
  cellMinDwellMs: 400,
  maxCellsPerBeat: 4,
  maxChainsPerTicket: 3,
  maxRollSheetEntries: 120,
  maxStoryBytes: 16384,
  carryTurnHours: 1,
  factSlackMs: 1000,
  echoChance: 0.35,
  validationMode: 'report',
  teamTriggers: true,
  absentBadOutcomeWeight: 1,
  tone: {
    targetMix: { disaster: 10, bad: 20, mixed: 25, good: 30, windfall: 15 },
    maxWindfallShare: 35,
  },
  windfall: { valuePerRunDay: 200, stashValueMax: 160, tradeBonusValueMax: 50, homeValueMax: 60 },
  trade: { shortPct: 50 },
  ally: {
    maxActive: 2,
    deliveries: 3,
    everyHours: 24,
    jitterHours: 4,
    deliveryValueMax: 40,
    echoAfterRaids: 2,
  },
});

/** shared-cs `PanelRulesDef` defaults (panels.html §2). Empty tiers and shots mean the code defaults. */
export const panelRulesDefaults = (): Required<PanelRulesDef> => ({
  maxActors: 6,
  maxBalloonsPerCell: 3,
  gutterFrac: 0.025,
  swipeFrac: 0.18,
  tiers: [],
  lowTierFirstCellMs: 150,
  cacheCells: 8,
  shots: [],
  poses: [],
  adjustLimits: null,
});

/** A new chain: one beat with a two-option choice, a table and an outcome each, so it validates except for tone. */
export function newBeatChain(id: string): BeatChainDef {
  return {
    id,
    title: 'New chain',
    status: 'dev_only',
    tags: [],
    eligible: null,
    cast: [{ role: 'companion', bind: 'squad_member', exclude: ['player', 'leader'], prefer: '' }],
    hidden: [],
    tells: [],
    root: 'b1',
    beats: {
      b1: {
        cells: [],
        choice: {
          prompt: '',
          timerS: 8,
          timeout: 'A',
          options: [
            { id: 'A', label: 'Option A', table: 'tA', toneNote: '', requiresLabel: '' },
            { id: 'B', label: 'Option B', table: 'tB', toneNote: '', requiresLabel: '' },
          ],
        },
        outcome: '',
      },
    },
    tables: {
      tA: [{ else: true, pick: [{ w: 1, outcome: 'oA' }] }],
      tB: [{ else: true, pick: [{ w: 1, outcome: 'oB' }] }],
    },
    outcomes: {
      oA: newOutcome('good'),
      oB: newOutcome('bad'),
    },
    strings: {},
  };
}

export const newOutcome = (tone = 'mixed') => ({
  tone,
  cells: [],
  tags: [],
  effects: [],
  reveals: [],
  next: '',
});

/** A new cell: a medium shot of the companion in the live room. */
export function newPanel(id: string): PanelSpec {
  return {
    id,
    layout: { slot: 'auto', insetOf: '', pageBreak: false, border: 'ink', mask: '' },
    shot: {
      type: 'medium',
      subject: 'companion',
      from: '',
      on: '',
      angle: 'eye',
      dutch: 0,
      side: 'auto',
    },
    actors: [
      {
        role: 'companion',
        mark: 'center',
        facing: 'camera',
        pose: 'stand',
        pairWith: '',
        expression: 'neutral',
        lookAt: 'camera',
        hold: 'weapon',
        marks: [],
        when: null,
      },
    ],
    set: { kind: 'room', template: '', dressing: true },
    lighting: { preset: 'day', from: '', backdrop: '' },
    effects: [],
    caption: null,
    dialogue: [],
    decision: false,
  };
}

export const newAlly = (id: string): AllyDef => ({
  id,
  name: 'New ally',
  look: 'from_role',
  deliveries: { count: 3, everyHours: 24, table: '', valueMax: 40 },
  echoes: [],
  factionId: null,
});

export const newPronounSet = (id: string): PronounSetDef => ({
  id,
  they: '',
  them: '',
  their: '',
  theirs: '',
  themself: '',
  plural: false,
});

export const newNpc = (id: string): NpcDef => ({
  id,
  name: 'New NPC',
  archetype: '',
  pronouns: 'they',
  lookSeed: 0,
  wear: [],
});
